import type { AgentActionButton, AgentChatEntry, AgentChatSummary, AgentMessageResponse, AgentSessionState } from '../types/agent';
import { apiService, type Project } from '../services/apiService';
import { HIDDEN_AGENT_ACTION_IDS } from './agentIntentOptions';
import {
  clearAgentProjectChatDismissed,
  isAgentProjectChatDismissed,
} from './agentProjectChatPrefs';

export const AGENT_CHAT_PAGE_SIZE = 30;

export const AGENT_LOCAL_WELCOME_TEXT =
  'Tell me what you want to do (for example: import Swagger, configure login, run full flow), and I will do it step by step. To start execution, create/import a project first.';

export const AGENT_LOCAL_WELCOME_ACTIONS: AgentActionButton[] = [
  { id: 'create_project_import', label: 'Create or Select Project', payload: { intent: 'create_project' } },
  { id: 'create_project_import', label: 'Import Swagger', payload: { intent: 'import_swagger' } },
  { id: 'create_environment', label: 'Create Environment', payload: { intent: 'open' } },
];

export const isExecuteRequestFollowUpActions = (actions: AgentActionButton[]): boolean =>
  actions.some((a) => a.id === 'run_single_request') &&
  actions.some((a) => a.id === 'run_collection');

export function filterAgentActionsForWidget(
  actions?: AgentActionButton[] | null
): AgentActionButton[] | undefined {
  if (!actions?.length) return undefined;
  if (isExecuteRequestFollowUpActions(actions)) {
    return undefined;
  }
  const filtered = actions.filter((action) => {
    if (HIDDEN_AGENT_ACTION_IDS.has(action.id)) return false;
    const label = action.label.trim().toLowerCase();
    return label !== 'retry request' && label !== 'open this request';
  });
  return filtered.length > 0 ? filtered : undefined;
}

export function normalizeAgentActions(
  actions?: AgentActionButton[] | null,
): AgentActionButton[] | undefined {
  if (!actions || actions.length === 0) return undefined;
  const normalized = actions.filter((a) => !HIDDEN_AGENT_ACTION_IDS.has(a.id));
  if (normalized.length === 0) return undefined;
  return normalized;
}

export function pickAgentChatForProject(
  chats: AgentChatSummary[],
  project?: { id: number; name: string } | null,
  session?: AgentSessionState | null
): AgentChatSummary | null {
  if (!chats.length) return null;

  const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';

  if (project?.name) {
    const projectName = normalize(project.name);
    const byProjectTitle = chats.find((c) => normalize(c.title) === projectName);
    if (byProjectTitle) return byProjectTitle;
  }

  const sessionMatchesSelectedProject =
    project != null &&
    ((project.id != null && session?.projectId === project.id) ||
      (project.name != null &&
        session?.projectName != null &&
        normalize(session.projectName) === normalize(project.name)));

  if (sessionMatchesSelectedProject && session?.chatId != null) {
    const bySessionChat = chats.find((c) => c.id === session.chatId);
    if (bySessionChat) return bySessionChat;
  }

  if (project?.name) {
    const projectName = normalize(project.name);
    const byPartialTitle = chats.find((c) => normalize(c.title).includes(projectName));
    if (byPartialTitle) return byPartialTitle;
  }

  if (!project) {
    if (session?.chatId != null) {
      const bySessionChat = chats.find((c) => c.id === session.chatId);
      if (bySessionChat) return bySessionChat;
    }
    if (session?.projectName) {
      const sessionProjectName = normalize(session.projectName);
      const bySessionProject = chats.find((c) => normalize(c.title) === sessionProjectName);
      if (bySessionProject) return bySessionProject;
    }
    return chats[0];
  }

  return null;
}

/** Map an agent chat thread to a project (chat title = project name). */
export function resolveProjectForAgentChat(
  chat: { id: number; title: string },
  projects: Array<{ id: number; name: string }>,
  session?: AgentSessionState | null,
): { id: number; name: string } | null {
  const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';
  const title = normalize(chat.title);

  if (title && title !== 'new chat') {
    const byTitle = projects.find((p) => normalize(p.name) === title);
    if (byTitle) return { id: byTitle.id, name: byTitle.name };
  }

  if (session?.chatId === chat.id && session.projectId != null) {
    const bySessionId = projects.find((p) => p.id === session.projectId);
    if (bySessionId) return { id: bySessionId.id, name: bySessionId.name };
    if (session.projectName) {
      return { id: session.projectId, name: session.projectName };
    }
  }

  if (session?.projectName) {
    const sessionName = normalize(session.projectName);
    if (title === sessionName) {
      const bySessionName = projects.find((p) => normalize(p.name) === sessionName);
      if (bySessionName) return { id: bySessionName.id, name: bySessionName.name };
    }
  }

  return null;
}

/** Always start a fresh chat thread titled with the new project name. */
export async function createAgentChatForNewProject(projectName: string): Promise<number> {
  const created = await apiService.agentCreateChat(projectName.trim());
  return created.id;
}

export type CreateProjectAgentChatResult = {
  project: Project;
  chatId: number;
  agentResponse: AgentMessageResponse;
};

/** Create a real project first, then title/link the agent chat (avoids orphan chats). */
export async function createProjectAndSyncAgentChat(
  projectName: string,
  existingChatId?: number | null,
): Promise<CreateProjectAgentChatResult> {
  const name = projectName.trim();
  if (!name) {
    throw new Error('Project name is required');
  }

  const project = await apiService.createProject({
    name,
    description: 'Created by AI Agent',
  });

  let chatId = existingChatId ?? null;
  if (chatId == null) {
    chatId = (await apiService.agentCreateChat(name)).id;
  } else {
    try {
      await apiService.agentRenameChat(chatId, name);
    } catch {
      /* title update is best-effort */
    }
  }

  const agentResponse = await apiService.agentMessage({
    action: 'select_project',
    chatId,
    payload: { projectId: project.id },
  });

  if (agentResponse.error) {
    throw new Error(agentResponse.reply || agentResponse.error || 'Failed to link project to AI Agent chat');
  }

  return { project, chatId, agentResponse };
}

export type EnsureAgentChatOptions = {
  userId?: number | null;
  /**
   * When true, recreate a listed chat even if the user previously deleted one for this project
   * (e.g. they explicitly selected the project again).
   */
  forceListed?: boolean;
  /** Reuse the active chat when continuing an in-progress conversation for the same project. */
  preferChatId?: number | null;
};

export type EnsureAgentChatResult = {
  chatId: number;
  created: boolean;
  /** False when only an empty unlisted draft was created (post-delete / no seed). */
  listed: boolean;
};

/** Prevents AI Agent page + Assistant from both seeding Sample Project at once. */
const ensureAgentChatInFlight = new Map<number, Promise<EnsureAgentChatResult>>();

/**
 * Pick an existing project chat or create one titled with the project name.
 * New users get a listed chat (persisted select_project reply) so Sample Project appears in the sidebar.
 * After the user deletes that chat, refreshes only create an unlisted draft unless forceListed.
 */
export async function ensureAgentChatForProject(
  project: { id: number; name: string },
  session?: AgentSessionState | null,
  options?: EnsureAgentChatOptions,
): Promise<EnsureAgentChatResult> {
  const existingInFlight = ensureAgentChatInFlight.get(project.id);
  if (existingInFlight) {
    return existingInFlight;
  }

  const run = (async (): Promise<EnsureAgentChatResult> => {
    try {
      return await ensureAgentChatForProjectUnlocked(project, session, options);
    } finally {
      ensureAgentChatInFlight.delete(project.id);
    }
  })();

  ensureAgentChatInFlight.set(project.id, run);
  return run;
}

async function ensureAgentChatForProjectUnlocked(
  project: { id: number; name: string },
  session?: AgentSessionState | null,
  options?: EnsureAgentChatOptions,
): Promise<EnsureAgentChatResult> {
  let chats = await apiService.agentListChats();
  const preferChatId = options?.preferChatId;
  if (preferChatId != null) {
    const preferred = chats.find((c) => c.id === preferChatId);
    if (preferred) {
      return { chatId: preferred.id, created: false, listed: true };
    }
  }
  let picked = pickAgentChatForProject(chats, project, session);
  if (picked) {
    await collapseDuplicateProjectTitleChats(chats, project, picked.id);
    return { chatId: picked.id, created: false, listed: true };
  }

  const userId = options?.userId ?? null;
  const dismissed =
    userId != null && isAgentProjectChatDismissed(userId, project.id);
  // Without a user id we cannot read the dismiss flag; prefer an unlisted draft
  // until auth is ready (effect re-runs with userId and seeds for new users).
  const shouldSeedListed =
    options?.forceListed === true || (userId != null && !dismissed);

  if (!shouldSeedListed) {
    const chatId = await createAgentChatForNewProject(project.name);
    return { chatId, created: true, listed: false };
  }

  // Another caller may have finished seeding between our first list and create.
  chats = await apiService.agentListChats();
  picked = pickAgentChatForProject(chats, project, session);
  if (picked) {
    await collapseDuplicateProjectTitleChats(chats, project, picked.id);
    return { chatId: picked.id, created: false, listed: true };
  }

  const chatId = await createAgentChatForNewProject(project.name);

  try {
    const res = await apiService.agentMessage({
      action: 'select_project',
      chatId,
      payload: { projectId: project.id },
    });
    if (res.error) {
      return { chatId, created: true, listed: false };
    }
  } catch {
    return { chatId, created: true, listed: false };
  }

  // Collapse any twin seeds created before this process held the lock.
  chats = await apiService.agentListChats();
  await collapseDuplicateProjectTitleChats(chats, project, chatId);

  if (userId != null) {
    clearAgentProjectChatDismissed(userId, project.id);
  }
  return { chatId, created: true, listed: true };
}

/** Keep one listed chat per project title; delete accidental duplicate seeds. */
async function collapseDuplicateProjectTitleChats(
  chats: AgentChatSummary[],
  project: { name: string },
  keepId: number,
): Promise<void> {
  const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';
  const projectName = normalize(project.name);
  if (!projectName) return;
  const extras = chats.filter(
    (c) => c.id !== keepId && normalize(c.title) === projectName,
  );
  await Promise.all(
    extras.map(async (chat) => {
      try {
        await apiService.agentDeleteChat(chat.id);
      } catch {
        /* ignore */
      }
    }),
  );
}

export function mapAgentHistoryEntries(entries: AgentChatEntry[]): AgentChatEntry[] {
  return entries.map((m) => {
    if (m.role !== 'agent') return m;
    const prelimFailure = isAgentFailurePrelim(m.text, m.actions);
    return {
      ...m,
      actions: normalizeAgentActions(m.actions),
    };
  });
}

function isAgentFailurePrelim(text: string, actions?: AgentActionButton[]): boolean {
  const normalized = text.trim().toLowerCase();
  return (
    normalized.startsWith('import failed:') ||
    normalized.startsWith('test failed.') ||
    normalized.startsWith('request executed with failure.') ||
    normalized.startsWith('login failed.')
  );
}
